import { useMutation, useQuery } from "@tanstack/react-query";
import {
  type ColumnDef,
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import clsx from "clsx";
import React, { useMemo, useState } from "react";
import {
  ChevronDown,
  ChevronUp,
  ThreeDotsVertical,
} from "react-bootstrap-icons";
import { Link } from "react-router-dom";
import {
  type DocumentDetail,
  DocumentStatus,
  DocumentSubType,
  ParentRelationship,
  Source,
} from "@/briefpoint-client";
import { useDocumentApi, useFirmApi } from "@/hooks/useApi";
import { useAuth } from "@/hooks/useAuth";
import {
  getArchivedCaseDocumentListOptions,
  getArchivedDocumentListOptions,
  getFirmCasesOptions,
} from "@/lib/query-options";
import { queryClient } from "@/lib/queryClient";
import type { DocumentClient } from "@/lib/types";
import {
  addUploadDate,
  getCaseJurisdiction,
  getDocumentStatusText,
  getSubTypeText,
  toLocaleUSDateString,
} from "@/lib/utils";
import ConfirmDeleteDocumentModal from "../ConfirmDeleteDocumentModal";
import { DocumentIcon } from "../DocumentIcon";
import DocumentListTableSupplementalRow from "../DocumentListTable/DocumentListTableSupplementalRow";
import Loading from "../Loading";
import Pagination from "../Pagination/Pagination";
import { Button } from "../ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuPortal,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import { toast } from "../ui/toast";
import styles from "./ArchiveDocumentList.module.scss";

interface ArchiveDocumentListProps {
  caseId?: string;
}

export default function ArchiveDocumentList(props: ArchiveDocumentListProps) {
  const documentApi = useDocumentApi();
  const [documentToDelete, setDocumentToDelete] = useState<
    DocumentClient | undefined
  >(undefined);
  const firmApi = useFirmApi();
  const auth = useAuth();

  const casesQuery = useQuery({
    ...getFirmCasesOptions(firmApi, auth.user?.firmId ?? ""),
  });

  const queryOptions = props.caseId
    ? getArchivedCaseDocumentListOptions(documentApi, props.caseId)
    : getArchivedDocumentListOptions(documentApi);

  const archivedDocumentsQuery = useQuery({
    ...queryOptions,
    select: (data) => {
      const docs = data
        .map((document) => addUploadDate(document as DocumentDetail))
        .filter((d) => d.status !== DocumentStatus.Error);

      return {
        documents: docs.filter(
          (doc) =>
            doc.parentRelationship !== ParentRelationship.SupplementalResponse,
        ),
        supplementalDocuments: docs.filter(
          (doc) =>
            doc.parentRelationship === ParentRelationship.SupplementalResponse,
        ),
      };
    },
  });

  const restoreMutation = useMutation({
    mutationFn: async (document: DocumentClient) => {
      await documentApi.documentRestore({
        documentId: document.id ?? "",
      });
      return document;
    },
    onSuccess: (document: DocumentClient) => {
      toast.success(`${document.name} was restored`);
    },
    onError: (document: DocumentDetail) => {
      toast.error(`Error restoring ${document.name}`);
    },
    onSettled: () => {
      queryClient.invalidateQueries(queryOptions);
    },
  });

  /* biome-ignore lint/correctness/useExhaustiveDependencies: we want to re-run the effect when the columns change */
  const columns: ColumnDef<DocumentDetail>[] = useMemo(
    () => [
      {
        header: "",
        accessorKey: "icon",
        accessorFn: (row) => row.source,
        meta: {
          width: "20px",
        },
        cell: ({ row }) => {
          return (
            <DocumentIcon
              extension={
                row.original.source === Source.InternalGenerated
                  ? "docx"
                  : "pdf"
              }
            />
          );
        },
      },
      {
        header: ({ column }) => {
          return (
            <Button
              className={styles.fieldHeaderButton}
              onClick={() =>
                column.toggleSorting(column.getIsSorted() === "asc")
              }
            >
              Document Name
              {column.getIsSorted() === "asc" ? <ChevronUp /> : <ChevronDown />}
            </Button>
          );
        },
        accessorKey: "name",
        accessorFn: (row) => row.name,
        meta: {
          width: "35%",
        },
        cell: ({ row }) => {
          return (
            <div data-testid="document-name" className={styles.cellData}>
              {row.original.name}
            </div>
          );
        },
        sortingFn: "text",
      },
      {
        header: ({ column }) => {
          return (
            <Button
              className={styles.fieldHeaderButton}
              onClick={() =>
                column.toggleSorting(column.getIsSorted() === "asc")
              }
            >
              Case Name
              {column.getIsSorted() === "asc" ? <ChevronUp /> : <ChevronDown />}
            </Button>
          );
        },
        accessorKey: "caseName",
        accessorFn: (row) =>
          casesQuery.data?.find((c) => c.id === row.caseId)?.shortTitle ?? "-",
        meta: {
          width: "20%",
        },
        cell: ({ row }) => {
          return (
            <div
              className={clsx(styles.cellData, styles.truncateText)}
              title={
                casesQuery.data?.find((c) => c.id === row.original.caseId)
                  ?.shortTitle ?? ""
              }
            >
              {casesQuery.data?.find((c) => c.id === row.original.caseId)
                ?.shortTitle ?? "-"}
            </div>
          );
        },
        sortingFn: "text",
      },
      {
        header: ({ column }) => {
          return (
            <Button
              className={styles.fieldHeaderButton}
              onClick={() =>
                column.toggleSorting(column.getIsSorted() === "asc")
              }
            >
              Document Type
              {column.getIsSorted() === "asc" ? <ChevronUp /> : <ChevronDown />}
            </Button>
          );
        },
        accessorKey: "documentType",
        accessorFn: (row) =>
          getSubTypeText(
            row.info?.documentSubType ?? DocumentSubType.Unknown,
            getCaseJurisdiction(casesQuery.data, row.caseId ?? "") ?? "",
          ).short,

        cell: ({ row }) => {
          return (
            <div className={styles.cellData}>
              {row.original.info?.documentSubType
                ? getSubTypeText(
                    row.original.info.documentSubType,
                    getCaseJurisdiction(
                      casesQuery.data,
                      row.original.caseId ?? "",
                    ) ?? "",
                  ).short
                : "Unknown"}
            </div>
          );
        },
        meta: {
          width: "20%",
        },
        sortingFn: "text",
      },
      {
        header: ({ column }) => {
          return (
            <Button
              className={styles.fieldHeaderButton}
              onClick={() =>
                column.toggleSorting(column.getIsSorted() === "asc")
              }
            >
              Upload Date
              {column.getIsSorted() === "asc" ? <ChevronUp /> : <ChevronDown />}
            </Button>
          );
        },
        accessorKey: "uploadDate",
        accessorFn: (row) => row.uploadDate?.getTime(),
        cell: ({ row }) => {
          return (
            <div className={styles.cellData}>
              {row.original.uploadDate
                ? toLocaleUSDateString(row.original.uploadDate)
                : "-"}
            </div>
          );
        },
        meta: {
          width: "13%",
        },
      },
      {
        header: ({ column }) => {
          return (
            <Button
              className={styles.fieldHeaderButton}
              onClick={() =>
                column.toggleSorting(column.getIsSorted() === "asc")
              }
            >
              Status{" "}
              {column.getIsSorted() === "asc" ? <ChevronUp /> : <ChevronDown />}
            </Button>
          );
        },
        accessorKey: "status",
        accessorFn: (row) => getDocumentStatusText(row.status).status,
        cell: ({ row }) => {
          return (
            <div className={styles.cellData}>
              {getDocumentStatusText(row.original.status).status}
            </div>
          );
        },
        meta: {
          width: "12%",
        },
      },
      {
        header: "",
        accessorKey: "actions",
        cell: ({ row }) => {
          return (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <ThreeDotsVertical size={14} color="var(--text)" />
              </DropdownMenuTrigger>
              <DropdownMenuPortal>
                <DropdownMenuContent>
                  <DropdownMenuItem
                    onClick={() =>
                      restoreMutation.mutate(row.original as DocumentClient)
                    }
                  >
                    Restore
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    className="text-danger"
                    onClick={() =>
                      setDocumentToDelete(row.original as DocumentClient)
                    }
                  >
                    Permanently Delete
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenuPortal>
            </DropdownMenu>
          );
        },
        meta: {
          width: "20px",
        },
      },
    ],
    [documentToDelete, casesQuery.data],
  );

  const table = useReactTable({
    data: archivedDocumentsQuery.data?.documents ?? [],
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    columns: columns,
    getCoreRowModel: getCoreRowModel(),
  });

  /* optimistically update the documents list */
  return (
    <Loading isLoading={archivedDocumentsQuery.isLoading}>
      <ConfirmDeleteDocumentModal
        showDeleteConfirm={!!documentToDelete}
        setShowDeleteConfirm={(show) =>
          setDocumentToDelete(show ? documentToDelete : undefined)
        }
        document={documentToDelete as DocumentClient}
      />
      {archivedDocumentsQuery.data?.documents.length ? (
        <>
          <Table>
            <TableHeader>
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <TableHead key={header.id}>
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext(),
                          )}
                    </TableHead>
                  ))}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody data-testid="archived-document-list-body">
              {table.getRowModel().rows.map((row) => (
                <React.Fragment key={row.id}>
                  <TableRow>
                    {row.getVisibleCells().map((cell) => (
                      <TableCell
                        key={cell.id}
                        style={{
                          width: (
                            cell.column.columnDef.meta as { width: string }
                          )?.width, // taken from column meta definition
                        }}
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext(),
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                  {archivedDocumentsQuery.data?.supplementalDocuments
                    ?.filter((doc) => doc.parentDocumentId === row.original.id)
                    .sort(
                      (a, b) => a.uploadDate.getTime() - b.uploadDate.getTime(),
                    )
                    .map((doc) => (
                      <DocumentListTableSupplementalRow
                        key={doc.id}
                        archived
                        row={row}
                        document={doc}
                      />
                    ))}
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
          <div className="d-flex justify-content-end pt-2">
            <Pagination
              navigate={(page) => table.setPageIndex(page - 1)}
              currentPage={table.getState().pagination.pageIndex + 1}
              totalPages={table.getPageCount()}
              totalItemCount={
                archivedDocumentsQuery.data?.documents.length ?? 0
              }
              itemsPerPage={table.getState().pagination.pageSize}
              showPageCount
              showInput
            />
          </div>
        </>
      ) : (
        <div>
          {props.caseId ? (
            <>
              This case has no archived documents. Please upload a document for
              this case
              <Link className="ps-1" to={`/cases/${props.caseId}`}>
                here
              </Link>
              .
            </>
          ) : (
            <>
              You have no archived documents. Please upload a document
              <Link className="ps-1" to="/documents">
                here
              </Link>
            </>
          )}
        </div>
      )}
    </Loading>
  );
}
